import React, { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../supabase';
import { useNavigate } from 'react-router-dom';
import { dedupeRequest, getCacheKey } from '../utils/queryDeduplication';
import { useUserProtocolStatus } from '../contexts/UserProtocolStatusContext';
import './PostFeed.css';
import './PostCard.css';
import CommentSection from './CommentSection';
import BiohackInfoCard from './BiohackInfoCard';
import ReportModal from './ReportModal';
import SharePostModal from './SharePostModal';
import { Icon } from './Icon.js';
import NotificationService from '../utils/NotificationService';
import PostImages from './PostImages';
import AttachmentDisplay from './AttachmentDisplay';
import { enrichBiomarkerWithRanges } from '../utils/biomarkerUtils';
import { escapeHtml } from '../utils/escapeHtml';
import { fetchBiomarkerAttachments } from '../utils/fetchBiomarkerAttachments';
import { getUserFacingErrorMessage } from '../utils/sanitizeErrorForLog';

// Helper to format time ago
function timeAgo(date) {
  const now = new Date();
  const d = new Date(date);
  const seconds = Math.floor((now - d) / 1000);
  if (seconds < 60) return 'Just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  const weeks = Math.floor(days / 7);
  if (weeks < 4) return `${weeks}w`;
  const months = Math.floor(days / 30);
  if (months >= 1 && months < 12) return `${months}mo`;
  if (months < 12) return `${weeks}w`; // 28–29 days: show 4w instead of 0mo
  const years = Math.floor(days / 365);
  return `${years}y`;
}

// Helper function to format post content with biohack mentions
const formatPostContentWithBiohacks = (content, biohackTags = []) => {
  if (!content) return '';
  
  // Always escape content first
  let escapedContent = escapeHtml(content);
  
  // If no biohack tags, convert newlines and return escaped content with line breaks
  if (!biohackTags || biohackTags.length === 0) {
    return escapedContent.replace(/\n/g, '<br>');
  }
  
  // Create a map of biohack names to biohack objects for lookup
  // Use a Set to track seen IDs to avoid processing duplicates
  const biohackMap = new Map();
  const seenIds = new Set();
  biohackTags.forEach(tag => {
    if (tag.biohacks?.name && tag.biohacks?.id) {
      // Only add if we haven't seen this biohack ID before (handle duplicates)
      if (!seenIds.has(tag.biohacks.id)) {
        biohackMap.set(tag.biohacks.name.toLowerCase(), tag.biohacks);
        seenIds.add(tag.biohacks.id);
      }
    }
  });
  
  const biohackNames = Array.from(biohackMap.keys());
  
  if (biohackNames.length === 0) return escapedContent.replace(/\n/g, '<br>');
  
  const escapeRegExp = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  // Get the actual names (not lowercased) for matching
  // Sort by length (longest first) to match longer names before shorter ones
  const actualNames = Array.from(biohackMap.keys())
    .map(name => biohackMap.get(name).name)
    .sort((a, b) => b.length - a.length);
  const names = actualNames.map(escapeRegExp);
  
  // Match biohack names as whole words
  const startBoundary = String.raw`(^|[\\s.,;:!?()[\\]{}"'])`;
  const endBoundary = String.raw`(?=\\s|$|[.,;:!?()[\\]{}"'])`;
  
  // Process each name separately to avoid regex issues with multiple alternations
  // This ensures each biohack is processed correctly, even when there are many
  let result = escapedContent;
  
  for (let i = 0; i < actualNames.length; i++) {
    const name = actualNames[i];
    const escapedName = names[i];
    const biohack = biohackMap.get(name.toLowerCase());
    
    if (!biohack || !biohack.id) continue;
    
    const pattern = new RegExp(`${startBoundary}(${escapedName})${endBoundary}`, 'gi');
    const escapedId = String(biohack.id).replace(/"/g, '&quot;');
    
    // Process matches manually to correctly handle positions
    let finalResult = '';
    let lastIndex = 0;
    let match;
    
    // Reset regex
    pattern.lastIndex = 0;
    
    while ((match = pattern.exec(result)) !== null) {
      const matchStart = match.index;
      const pre = match[1];
      const matchedName = match[2];
      
      // Add text before this match
      finalResult += result.substring(lastIndex, matchStart);
      
      // Check if we're already inside a span tag (avoid double-wrapping)
      const beforeMatch = finalResult;
      const lastSpanOpen = beforeMatch.lastIndexOf('<span class="biohack-mention"');
      const lastSpanClose = beforeMatch.lastIndexOf('</span>');
      
      // If we're already inside a span tag, don't wrap again
      if (lastSpanOpen > lastSpanClose) {
        finalResult += match[0];
      } else {
        // Replace with biohack mention span
        finalResult += `${pre}<span class="biohack-mention" data-biohack-id="${escapedId}" data-biohack-name="${escapeHtml(biohack.name)}">${matchedName}</span>`;
      }
      
      lastIndex = matchStart + match[0].length;
    }
    
    // Add remaining text
    finalResult += result.substring(lastIndex);
    result = finalResult;
  }
  
  // Convert newlines to <br> tags to preserve line breaks in HTML
  result = result.replace(/\n/g, '<br>');
  
  return result;
};

const PostCard = ({ post, attachments = [], likesInfo, followStatusForAuthor, onFollowStateChange, onCountsChanged, userVotes, handleVote, originalPostBySharedFrom = {} }) => {
  const username = post.user_profiles?.username || 'User';
  const userIdForProfile = post.user_profiles?.user_id || post.user_id;
  // Use user_level from user_profiles, fallback to post.level, then default to 1
  const level = post.user_profiles?.user_level ?? post.level ?? 1;
  const date = post.created_at ? timeAgo(post.created_at) : '';
  
  // Format content with bold biohacks
  const formattedContent = formatPostContentWithBiohacks(
    post.content,
    post.post_biohack_tags || []
  );

  const [liked, setLiked] = useState(false);
  const [likeId, setLikeId] = useState(null);
  const [userId, setUserId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(post.title || '');
  const [editContent, setEditContent] = useState(post.content || '');
  const [error, setError] = useState(null);
  const [reposted, setReposted] = useState(false);
  const [repostId, setRepostId] = useState(null);
  const [repostLoading, setRepostLoading] = useState(false);
  const [localRepostCount, setLocalRepostCount] = useState(post.reposts_count || 0);
  // Local counts for optimistic UI updates
  const [localLikesCount, setLocalLikesCount] = useState(post.likes_count || 0);
  const [localCommentsCount, setLocalCommentsCount] = useState(post.comments_count || 0);
  // Module item expand/collapse state
  const [expandedModules, setExpandedModules] = useState({});
  // BiohackInfoCard modal state
  const [showBiohackInfo, setShowBiohackInfo] = useState(false);
  const [selectedBiohack, setSelectedBiohack] = useState(null);
  // Report modal state
  const [showReportModal, setShowReportModal] = useState(false);
  // Share modal state
  const [showShareModal, setShowShareModal] = useState(false);
  // Favorites state
  const [favourites, setFavourites] = useState([]);
  // Rated biohack IDs (for BiohackInfoCard star state when opened from post)
  const [ratedBiohackIds, setRatedBiohackIds] = useState(new Set());
  // Comment input state
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const commentRefreshFnRef = useRef(null);
  const [originalPostInfo, setOriginalPostInfo] = useState(post.originalPost || null);
  const [originalPost, setOriginalPost] = useState(post.originalPostData || null);
  const [originalPostNotVisible, setOriginalPostNotVisible] = useState(false);
  // FAB menu state
  const [isFabMenuOpen, setIsFabMenuOpen] = useState(false);
  const fabMenuRef = useRef(null);
  const fabButtonRef = useRef(null);
  // Visibility FAB menu state
  const [isVisibilityFabOpen, setIsVisibilityFabOpen] = useState(false);
  const visibilityFabMenuRef = useRef(null);
  const visibilityFabButtonRef = useRef(null);
  const [postVisibility, setPostVisibility] = useState(post.visibility || 'public');
  // Biotags dropdown state
  const [showBiotagsDropdown, setShowBiotagsDropdown] = useState(false);
  const biotagsDropdownRef = useRef(null);
  const biotagsButtonRef = useRef(null);
  
  // Update postVisibility when post prop changes
  useEffect(() => {
    if (post.visibility) {
      setPostVisibility(post.visibility);
    }
  }, [post.visibility]);
  // Follow state
  const [isFollowing, setIsFollowing] = useState(false);
  const [isFollowPending, setIsFollowPending] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  
  // Disclaimer modal state
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);
  const [disclaimerText, setDisclaimerText] = useState('');
  const [disclaimerVersion, setDisclaimerVersion] = useState('');
  const [acceptResponsibility, setAcceptResponsibility] = useState(false);
  const [doNotShowAgain, setDoNotShowAgain] = useState(false);
  const [pendingProtocol, setPendingProtocol] = useState(null);

  const navigate = useNavigate();

  // Compute the time ago label for the post, showing (edited) if updated
  let timeAgoLabel = '';
  if (post.updated_at && post.updated_at !== post.created_at) {
    timeAgoLabel = `${timeAgo(post.updated_at)} `;
  } else {
    timeAgoLabel = timeAgo(post.created_at);
  }
  const isEdited = post.updated_at && post.updated_at !== post.created_at;

  // Get post author's gender from post data (already fetched in PostFeed)
  const postAuthorGender = post.user_profiles?.gender || null;

  // Use likesInfo from parent if available (fetched in batch), otherwise fetch individually
  useEffect(() => {
    // If likesInfo is provided from parent (PostFeed), use it to avoid duplicate requests
    if (likesInfo) {
      setLiked(likesInfo.liked || false);
      setLikeId(likesInfo.likeId || null);
      setReposted(likesInfo.reposted || false);
      setRepostId(likesInfo.repostId || null);
    }
  }, [likesInfo]);

  // Fetch current user ID (needed for various operations)
  // Note: Like and repost status are now handled via likesInfo prop from PostFeed (batched)
  useEffect(() => {
    let isMounted = true;
    const fetchUserId = async () => {
      // Use getSession() instead of getUser() to avoid network requests - session is cached
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user;
      if (!user) {
        setUserId(null);
        // Only clear liked/likeId/reposted if likesInfo wasn't provided (fallback case)
        if (!likesInfo) {
          setLiked(false);
          setLikeId(null);
          setReposted(false);
          setRepostId(null);
        }
        return;
      }
      setUserId(user.id);
    };
    fetchUserId();
    return () => { isMounted = false; };
  }, [likesInfo]);

  // Sync local counts when post prop changes
  useEffect(() => {
    setLocalRepostCount(post.reposts_count || 0);
    setLocalLikesCount(post.likes_count || 0);
    setLocalCommentsCount(post.comments_count || 0);
  }, [post.reposts_count, post.likes_count, post.comments_count]);

  // Track if we've fetched original post to avoid duplicate fetches
  const hasFetchedOriginalPostRef = useRef(false);
  const lastSharedFromRef = useRef(null);

  // Ensure we have original post info for reposts when refreshing
  useEffect(() => {
    let isMounted = true;

    // Reset fetch flag if shared_from changed to a different post
    if (post.shared_from !== lastSharedFromRef.current) {
      hasFetchedOriginalPostRef.current = false;
      lastSharedFromRef.current = post.shared_from;
    }

    // Handle post.originalPost prop (from parent)
    if (post.originalPost) {
      setOriginalPostInfo(prev => {
        if (
          prev &&
          prev.user_id === post.originalPost.user_id &&
          prev.username === post.originalPost.username
        ) {
          return prev;
        }
        return post.originalPost;
      });
    }

    // Handle post.originalPostData prop (from parent – e.g. after reposting or when feed passed it)
    if (post.originalPostData) {
      setOriginalPost(post.originalPostData);
      setOriginalPostNotVisible(false);
      hasFetchedOriginalPostRef.current = true;
      return () => { isMounted = false; };
    }

    // If not a repost, clear state
    if (!post.shared_from) {
      setOriginalPostInfo(null);
      setOriginalPost(null);
      setOriginalPostNotVisible(false);
      hasFetchedOriginalPostRef.current = false;
      lastSharedFromRef.current = null;
      return () => { isMounted = false; };
    }

    // Use batched original post from PostFeed when available (avoids per-card request)
    const fromMap = originalPostBySharedFrom && originalPostBySharedFrom[post.shared_from];
    if (fromMap) {
      setOriginalPostInfo({
        user_id: fromMap.user_id,
        username: fromMap.user_profiles?.username || 'Unknown User',
        profile_picture_url: fromMap.user_profiles?.profile_picture_url
      });
      setOriginalPost(fromMap);
      setOriginalPostNotVisible(false);
      hasFetchedOriginalPostRef.current = true;
      return () => { isMounted = false; };
    }

    // Batch ran but this original wasn't returned (RLS: viewer can't see it)
    const mapHasEntries = originalPostBySharedFrom && typeof originalPostBySharedFrom === 'object' && Object.keys(originalPostBySharedFrom).length > 0;
    if (mapHasEntries && post.shared_from && !(post.shared_from in originalPostBySharedFrom)) {
      hasFetchedOriginalPostRef.current = true;
      setOriginalPostNotVisible(true);
      return () => { isMounted = false; };
    }

    // Fallback: fetch single original post (e.g. when not from feed batch or RLS returned partial)
    if (!hasFetchedOriginalPostRef.current && post.shared_from) {
      const fetchOriginalPostInfo = async () => {
        try {
          const cacheKey = getCacheKey('fetchOriginalPost', post.shared_from);
          const { data, error } = await dedupeRequest(cacheKey, async () => {
            return await supabase
              .from('posts')
              .select(`
                id, user_id, title, content, created_at,
                user_profiles!posts_user_id_fkey(username, user_level, profile_picture_url, gender),
                post_attachments:post_attachments_post_id_fkey(
                  id, type, sort_order,
                  post_attachment_protocols:post_attachment_protocols_post_attachment_id_fkey(
                    protocols:protocol_id(
                      id, name, description, created_at, adherence_score, pop_score,
                      protocol_hacks:protocol_hacks_protocol_id_fkey(
                        id, position,
                        biohacks:protocol_hacks_hack_id_fkey(id, name, image_url, category)
                      ),
                      protocol_ratings ( id, rating ),
                      user_profiles!protocols_created_by_fkey(user_id, username)
                    )
                  ),
                  post_attachment_images:post_attachment_images_post_attachment_id_fkey(
                    id, image_url, file_name, mime_type
                  ),
                  post_attachment_reports:post_attachment_reports_post_attachment_id_fkey(
                    protocol_report_snapshots:report_id(
                      id, protocol_id, title, snapshot_date, created_at
                    )
                  )
                ),
                post_biohack_tags:post_biohack_tags_post_id_fkey(
                  biohacks:biohack_id(id, name, image_url, category)
                ),
                post_biomarker_tags:post_biomarker_tags_post_id_fkey(
                  biomarkers:biomarker_id(id, name, category, unit)
                )
              `)
              .eq('id', post.shared_from)
              .single();
          });

          if (!error && data && isMounted && !hasFetchedOriginalPostRef.current) {
            setOriginalPostInfo({
              user_id: data.user_id,
              username: data.user_profiles?.username || 'Unknown User',
              profile_picture_url: data.user_profiles?.profile_picture_url
            });
            let postToSet = data;
            try {
              const biomarkerData = await fetchBiomarkerAttachments([data.id], [data]);
              if (biomarkerData[data.id] && Object.keys(biomarkerData[data.id]).length > 0) {
                const attachmentMap = new Map((data.post_attachments || []).map((att) => [att.id, { ...att }]));
                Object.values(biomarkerData[data.id]).forEach(({ attachment, biomarkers }) => {
                  const enriched = {
                    post_attachment_biomarkers: (biomarkers || []).map((bm) => ({
                      biomarker_id: bm.id,
                      biomarkers: bm
                    }))
                  };
                  const existing = attachmentMap.get(attachment.id);
                  if (existing) {
                    Object.assign(existing, enriched);
                    attachmentMap.set(attachment.id, existing);
                  } else {
                    attachmentMap.set(attachment.id, {
                      id: attachment.id,
                      post_id: attachment.post_id,
                      type: attachment.type,
                      sort_order: attachment.sort_order ?? 0,
                      ...enriched
                    });
                  }
                });
                const sorted = Array.from(attachmentMap.values()).sort(
                  (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)
                );
                postToSet = { ...data, post_attachments: sorted };
              }
            } catch (bioErr) {
              console.error('Error fetching biomarker data for original post:', bioErr);
            }
            setOriginalPost(postToSet);
            setOriginalPostNotVisible(false);
            hasFetchedOriginalPostRef.current = true;
          } else if (isMounted && !hasFetchedOriginalPostRef.current) {
            hasFetchedOriginalPostRef.current = true;
            setOriginalPostNotVisible(true);
          }
        } catch (err) {
          console.error('Error fetching original post info:', err);
          if (isMounted && !hasFetchedOriginalPostRef.current) {
            hasFetchedOriginalPostRef.current = true;
            setOriginalPostNotVisible(true);
          }
        }
      };

      fetchOriginalPostInfo();
    }

    return () => {
      isMounted = false;
    };
  }, [post.shared_from, post.originalPost, post.originalPostData, originalPostBySharedFrom]);

  // Fetch favorites function - use deduplication to prevent duplicate requests
  const fetchFavourites = useCallback(async () => {
    if (!userId) return;
    
    // Use deduplication to prevent multiple simultaneous requests
    const cacheKey = getCacheKey('fetchFavourites', userId);
    
    return dedupeRequest(cacheKey, async () => {
      try {
        const { data, error } = await supabase
          .from('hack_favourited')
          .select('hack_id')
          .eq('user_id', userId);

        if (error) throw error;
        const favouritesData = data || [];
        setFavourites(favouritesData);
        return favouritesData;
      } catch (error) {
        console.error('Error fetching favourites:', error);
        setFavourites([]);
        return [];
      }
    });
  }, [userId]);

  // Fetch favorites when user changes
  useEffect(() => {
    if (userId) {
      fetchFavourites();
    }
  }, [userId, fetchFavourites]);

  // Fetch user's rated biohack IDs for BiohackInfoCard star state
  useEffect(() => {
    const fetchUserRatings = async () => {
      if (!userId) return;
      try {
        const cacheKey = getCacheKey('fetchUserRatings', userId);
        const data = await dedupeRequest(cacheKey, async () => {
          const { data: ratingData, error } = await supabase
            .from('hack_scoring')
            .select('hack_id')
            .eq('user_id', userId);
          if (error) throw error;
          return ratingData || [];
        });
        setRatedBiohackIds(new Set((data || []).map(r => r.hack_id)));
      } catch (error) {
        console.error('Error fetching user ratings:', error);
      }
    };
    fetchUserRatings();
  }, [userId]);

  // Toggle favourite function
  const handleToggleFavourite = async (hackId) => {
    try {
      if (!userId) return;

      const isFavourited = favourites.some(fav => fav.hack_id === hackId);

      if (isFavourited) {
        // Remove from favourites
        const { error } = await supabase
          .from('hack_favourited')
          .delete()
          .eq('user_id', userId)
          .eq('hack_id', hackId);

        if (error) throw error;
        setFavourites(favourites.filter(fav => fav.hack_id !== hackId));
      } else {
        // Add to favourites
        const { error } = await supabase
          .from('hack_favourited')
          .insert({
            user_id: userId,
            hack_id: hackId,
            timestamp: new Date().toISOString()
          });

        if (error) throw error;
        setFavourites([...favourites, { hack_id: hackId }]);
      }
    } catch (error) {
      console.error('Error toggling favourite:', error);
    }
  };

  // Like/unlike handler
  const handleLikeToggle = async () => {
    if (!userId || loading) {
      if (!userId) console.warn('User not signed in');
      return;
    }
    setLoading(true);
    
    try {
      if (!liked) {
        // Like the post
        const { data, error } = await supabase
          .from('post_likes')
          .insert({ user_id: userId, post_id: post.id })
          .select('id')
          .single();
        
        if (!error && data) {
          // Note: likes_count is automatically updated by database trigger
          // Note: Notification is automatically created by database trigger
          
          // Update local state optimistically
          setLiked(true);
          setLikeId(data.id);
          // Update likes count optimistically for instant UI feedback
          setLocalLikesCount(prev => prev + 1);
          // Don't call onCountsChanged here - real-time subscription will handle count updates
          // Only call if we need to trigger a refresh in parent (e.g., for cache invalidation)
          // onCountsChanged is already handled by real-time subscription in PostFeed
        } else if (error) {
          console.error('Like error:', error);
        }
      } else {
        // Unlike the post
        const { error } = await supabase
          .from('post_likes')
          .delete()
          .eq('id', likeId);
        
        if (!error) {
          // Note: likes_count is automatically updated by database trigger
          
          // Update local state optimistically
          setLiked(false);
          setLikeId(null);
          // Update likes count optimistically for instant UI feedback
          setLocalLikesCount(prev => Math.max(0, prev - 1));
          // Don't call onCountsChanged here - real-time subscription will handle count updates
        } else {
          console.error('Unlike error:', error);
        }
      }
    } catch (error) {
      console.error('Unexpected error in handleLikeToggle:', error);
    }
    
    setLoading(false);
  };

  // Handle comment count changes (called from CommentSection)
  const handleCommentCountChange = useCallback((delta) => {
    // Delta: +1 for add, -1 for delete
    setLocalCommentsCount(prev => Math.max(0, prev + delta));
  }, []);

  // Add a new comment handler
  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!userId || !newComment.trim()) return;
    setSubmittingComment(true);
    const { error } = await supabase
      .from('post_comments')
      .insert({ post_id: post.id, user_id: userId, content: newComment.trim() });
    setSubmittingComment(false);
    if (!error) {
      // Note: Notification is automatically created by database trigger
      
      setNewComment('');
      // Update comments count optimistically for instant UI feedback
      setLocalCommentsCount(prev => prev + 1);
      // Refresh comments if function is available - this already fetches comments
      if (commentRefreshFnRef.current) {
        commentRefreshFnRef.current();
      }
      // Don't call onCountsChanged here - commentRefreshFnRef already refreshes comments,
      // and real-time subscription will handle count updates. This avoids duplicate fetching.
    } else {
      console.error('Failed to add comment:', error);
    }
  };

  // Repost handler - opens share modal
  const handleRepost = () => {
    if (!userId) {
      console.warn('User not signed in');
      return;
    }
    
    if (reposted) {
      // If already reposted, remove the repost
      handleRemoveRepost();
    } else {
      // Open share modal
      setShowShareModal(true);
    }
  };

  // Handle removing a repost
  const handleRemoveRepost = async () => {
    if (!userId || repostLoading) return;
    
    setRepostLoading(true);
    
    try {
      // Remove repost by deleting the repost post
      const { error } = await supabase
        .from('posts')
        .delete()
        .eq('id', repostId);
      
      if (!error) {
        // Note: reposts_count is automatically updated by database trigger
        
        setReposted(false);
        setRepostId(null);
        
        // Update the local repost count immediately for instant UI update
        setLocalRepostCount(prev => Math.max(0, prev - 1));
        
        // Notify parent to remove the repost post from the feed and refresh counts
        // This is needed to update the feed and refresh the original post's repost count
        if (onCountsChanged) {
          onCountsChanged({ removeRepostId: repostId });
        }
      } else {
        console.error('Remove repost error:', error);
      }
    } catch (error) {
      console.error('Unexpected error in handleRemoveRepost:', error);
    }
    
    setRepostLoading(false);
  };

  // Handle sharing post with user's title and text
  const handleSharePost = async (shareText, shareTitle) => {
    if (!userId || repostLoading) {
      throw new Error('User not signed in or repost already in progress');
    }
    
    setRepostLoading(true);
    
    try {
      // Create a repost post that references the original
      const repostData = {
        user_id: userId,
        content: shareText || '', // User's text (can be empty)
        title: (shareTitle && shareTitle.trim()) ? shareTitle.trim() : '', // From modal; DB requires NOT NULL
        shared_from: post.id,
        visibility: 'followers',
        has_attachment: false // Shared posts don't copy attachments
      };
      
      const { data: repostPost, error: repostError } = await supabase
        .from('posts')
        .insert(repostData)
        .select('id')
        .single();
      
      if (!repostError && repostPost) {
        // Note: reposts_count is automatically updated by database trigger
        
        setReposted(true);
        setRepostId(repostPost.id);
        
        // Update the local repost count immediately for instant UI update
        setLocalRepostCount(prev => prev + 1);
        
        // Get current user info for the repost post - use getSession() to avoid network request
        await supabase.auth.getSession();
        const { data: userProfile } = await supabase
          .from('user_profiles')
          .select('user_id, username, user_level, profile_picture_url, gender')
          .eq('user_id', userId)
          .maybeSingle();
        
        // Create the repost post object to add to the feed
        const newRepostPost = {
          id: repostPost.id,
          user_id: userId,
          content: repostData.content,
          title: repostData.title,
          shared_from: post.id,
          visibility: 'followers',
          has_attachment: false,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          likes_count: 0,
          comments_count: 0,
          reposts_count: 0,
          is_deleted: false,
          // Important: PostCard renders from post.user_profiles, not post.users
          user_profiles: userProfile || {
            user_id: userId,
            username: 'Unknown User',
            user_level: 1,
            profile_picture_url: null,
            gender: null
          },
          // Store original post info for display
          originalPost: {
            user_id: post.user_id,
            username: post.user_profiles?.username || 'Unknown User'
          },
          originalPostData: {
            ...post
          }
        };
        
        // Call onCountsChanged with the new repost post data
        // This is needed to add the new repost to the feed and refresh original post counts
        if (onCountsChanged) {
          onCountsChanged(newRepostPost);
        }

        // Send notification to original post author
        if (post.user_id !== userId) {
          await NotificationService.notifyPostShare(userId, post.id, post.user_id);
        }
      } else if (repostError) {
        console.error('Repost error:', repostError);
        throw new Error('Failed to share post. Please try again.');
      }
    } catch (error) {
      console.error('Unexpected error in handleSharePost:', error);
      throw error;
    } finally {
      setRepostLoading(false);
    }
  };

  // Handler for clicking username
  const handleUsernameClick = (e) => {
    e.stopPropagation();
    if (userIdForProfile) {
      navigate(`/user/${userIdForProfile}`);
    }
  };

  const handleOriginalPosterClick = (e) => {
    e.stopPropagation();
    if (originalPostInfo?.user_id) {
      navigate(`/user/${originalPostInfo.user_id}`);
    }
  };

  // Sync edit fields when post prop changes (but not while editing)
  useEffect(() => {
    if (!editing) {
      setEditTitle(post.title || '');
      setEditContent(post.content || '');
    }
  }, [post.title, post.content, editing]);

  // Edit post handlers
  const handleEditPost = () => {
    setEditing(true);
    setEditTitle(post.title || '');
    setEditContent(post.content || '');
    setError(null);
  };
  const handleCancelEdit = () => {
    setEditing(false);
    setError(null);
  };
  const handleSaveEdit = async () => {
    if (!editContent.trim()) return;
    const { error } = await supabase
      .from('posts')
      .update({ title: editTitle.trim(), content: editContent.trim(), updated_at: new Date().toISOString() })
      .eq('id', post.id);
    if (!error) {
      // Fetch the updated post to get all fields
      const { data: updatedPost, error: fetchError } = await supabase
        .from('posts')
        .select(`
          *,
          user_profiles (
            user_id,
            username,
            profile_picture_url,
            user_level,
            gender
          )
        `)
        .eq('id', post.id)
        .single();
      
      if (!fetchError && updatedPost) {
        setEditing(false);
        setError(null);
        // Update local state immediately
        if (onCountsChanged) {
          onCountsChanged({ updatedPost });
        }
      } else {
        setEditing(false);
        setError(null);
        // Fallback: still notify parent even if fetch failed
        if (onCountsChanged) {
          onCountsChanged({ updatedPost: { ...post, title: editTitle.trim(), content: editContent.trim() } });
        }
      }
    } else {
      setError('Failed to update post');
    }
  };
  // Delete post handler (soft delete)
  const handleDeletePost = async () => {
    const { error } = await supabase
      .from('posts')
      .update({ is_deleted: true, content: '[deleted]', title: '[deleted]' })
      .eq('id', post.id);
    if (!error) {
      setEditing(false);
      setError(null);
      // Pass the deleted post ID to onCountsChanged so the parent can remove it from the feed
      if (onCountsChanged) onCountsChanged({ deletedPostId: post.id });
    } else {
      setError('Failed to delete post');
    }
  };

  const isOwnPost = userId === post.user_id;
  const isOwnProfile = userId === userIdForProfile;

  // Check if current user is following the post author
  // When followStatusForAuthor is provided by PostFeed (batched), use it and skip the per-card fetch
  useEffect(() => {
    if (!userId || !userIdForProfile || isOwnProfile) {
      setIsFollowing(false);
      setIsFollowPending(false);
      return;
    }

    if (followStatusForAuthor !== undefined) {
      setIsFollowing(followStatusForAuthor.isFollowing);
      setIsFollowPending(followStatusForAuthor.isPending ?? false);
      return;
    }

    const checkFollowing = async () => {
      try {
        const { data, error } = await supabase
          .from('follows')
          .select('follower_id, accepted_at')
          .eq('follower_id', userId)
          .eq('followee_id', userIdForProfile)
          .maybeSingle();

        if (error) throw error;
        if (data) {
          setIsFollowing(true);
          setIsFollowPending(data.accepted_at === null); // Pending if accepted_at is null
        } else {
          setIsFollowing(false);
          setIsFollowPending(false);
        }
      } catch (error) {
        console.error('Error checking follow status:', error);
        setIsFollowing(false);
        setIsFollowPending(false);
      }
    };

    checkFollowing();
  }, [userId, userIdForProfile, isOwnProfile, followStatusForAuthor]);

  // Listen for follow state changes from other PostCards (shared across both batched and per-card flows)
  useEffect(() => {
    const handleFollowStateChange = (event) => {
      const { followerId, followeeId, isFollowing: newFollowingState, isFollowPending: newPendingState } = event.detail;
      if (followerId === userId && followeeId === userIdForProfile) {
        setIsFollowing(newFollowingState);
        setIsFollowPending(newPendingState !== undefined ? newPendingState : false);
      }
    };

    window.addEventListener('followStateChanged', handleFollowStateChange);
    return () => window.removeEventListener('followStateChanged', handleFollowStateChange);
  }, [userId, userIdForProfile]);

  // Handle follow/unfollow toggle
  const handleFollowToggle = async (e) => {
    e.stopPropagation();
    if (!userId || !userIdForProfile || isOwnProfile || followLoading) return;

    setFollowLoading(true);
    const wasFollowing = isFollowing;
    const wasPending = isFollowPending;
    const newFollowingState = !wasFollowing;

    // Optimistic update
    setIsFollowing(newFollowingState);
    // When following, assume it's pending until we get the actual response
    setIsFollowPending(newFollowingState && !wasFollowing);

    // Dispatch event to sync other PostCards
    window.dispatchEvent(new CustomEvent('followStateChanged', {
      detail: {
        followerId: userId,
        followeeId: userIdForProfile,
        isFollowing: newFollowingState,
        isFollowPending: newFollowingState && !wasFollowing
      }
    }));

    try {
      if (!wasFollowing) {
        // Follow the user
        const { data, error } = await supabase
          .from('follows')
          .insert({ follower_id: userId, followee_id: userIdForProfile })
          .select('accepted_at')
          .single();

        if (error) {
          setIsFollowing(wasFollowing); // Revert on error
          setIsFollowPending(wasPending);
          // Revert the event too
          window.dispatchEvent(new CustomEvent('followStateChanged', {
            detail: {
              followerId: userId,
              followeeId: userIdForProfile,
              isFollowing: wasFollowing,
              isFollowPending: wasPending
            }
          }));
          console.error('Follow error:', error);
          alert(getUserFacingErrorMessage(error, 'Could not follow user. Please try again.'));
        } else if (data) {
          // Update pending state based on actual response
          const isPending = data.accepted_at === null;
          setIsFollowPending(isPending);
          const status = { isFollowing: true, isPending };
          window.dispatchEvent(new CustomEvent('followStateChanged', {
            detail: {
              followerId: userId,
              followeeId: userIdForProfile,
              isFollowing: true,
              isFollowPending: isPending
            }
          }));
          onFollowStateChange?.(userIdForProfile, status);
        }
        // Note: Notification is automatically created by database trigger
      } else {
        // Unfollow the user
        const { error } = await supabase
          .from('follows')
          .delete()
          .eq('follower_id', userId)
          .eq('followee_id', userIdForProfile);

        if (error) {
          setIsFollowing(wasFollowing); // Revert on error
          setIsFollowPending(wasPending);
          // Revert the event too
          window.dispatchEvent(new CustomEvent('followStateChanged', {
            detail: {
              followerId: userId,
              followeeId: userIdForProfile,
              isFollowing: wasFollowing,
              isFollowPending: wasPending
            }
          }));
          console.error('Unfollow error:', error);
          alert(getUserFacingErrorMessage(error, 'Could not unfollow user. Please try again.'));
        } else {
          setIsFollowPending(false);
          const status = { isFollowing: false, isPending: false };
          window.dispatchEvent(new CustomEvent('followStateChanged', {
            detail: {
              followerId: userId,
              followeeId: userIdForProfile,
              isFollowing: false,
              isFollowPending: false
            }
          }));
          onFollowStateChange?.(userIdForProfile, status);
        }
      }
    } catch (error) {
      setIsFollowing(wasFollowing); // Revert on error
      setIsFollowPending(wasPending);
      // Revert the event too
      window.dispatchEvent(new CustomEvent('followStateChanged', {
        detail: {
          followerId: userId,
          followeeId: userIdForProfile,
          isFollowing: wasFollowing,
          isFollowPending: wasPending
        }
      }));
      console.error('Unexpected error in handleFollowToggle:', error);
    } finally {
      setFollowLoading(false);
    }
  };

  // Handle FAB button click
  const handleFabButtonClick = (e) => {
    e.stopPropagation();
    setIsFabMenuOpen(prev => !prev);
  };

  // Close FAB menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        isFabMenuOpen &&
        fabMenuRef.current &&
        !fabMenuRef.current.contains(e.target) &&
        fabButtonRef.current &&
        !fabButtonRef.current.contains(e.target)
      ) {
        setIsFabMenuOpen(false);
      }
      
      // Close visibility FAB menu when clicking outside
      if (
        isVisibilityFabOpen &&
        visibilityFabMenuRef.current &&
        !visibilityFabMenuRef.current.contains(e.target) &&
        visibilityFabButtonRef.current &&
        !visibilityFabButtonRef.current.contains(e.target)
      ) {
        setIsVisibilityFabOpen(false);
      }
      
      // Close biotags dropdown when clicking outside
      if (
        showBiotagsDropdown &&
        biotagsDropdownRef.current &&
        !biotagsDropdownRef.current.contains(e.target) &&
        biotagsButtonRef.current &&
        !biotagsButtonRef.current.contains(e.target)
      ) {
        setShowBiotagsDropdown(false);
      }
    };

    if (isFabMenuOpen || isVisibilityFabOpen || showBiotagsDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isFabMenuOpen, isVisibilityFabOpen, showBiotagsDropdown]);

  // Close FAB menu on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        if (isFabMenuOpen) {
          setIsFabMenuOpen(false);
        }
        if (isVisibilityFabOpen) {
          setIsVisibilityFabOpen(false);
        }
        if (showBiotagsDropdown) {
          setShowBiotagsDropdown(false);
        }
      }
    };

    if (isFabMenuOpen || isVisibilityFabOpen || showBiotagsDropdown) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isFabMenuOpen, isVisibilityFabOpen, showBiotagsDropdown]);

  // Handle menu item clicks
  const handleReportClick = () => {
    setIsFabMenuOpen(false);
    setShowReportModal(true);
  };

  const handleEditClick = () => {
    setIsFabMenuOpen(false);
    handleEditPost();
  };

  const handleDeleteClick = () => {
    setIsFabMenuOpen(false);
    handleDeletePost();
  };

  // Handle visibility change
  const handleVisibilityChange = async (newVisibility) => {
    if (!userId || userId !== post.user_id) return;
    
    try {
      setLoading(true);
      const { error } = await supabase
        .from('posts')
        .update({ visibility: newVisibility })
        .eq('id', post.id)
        .eq('user_id', userId);

      if (error) throw error;

      setPostVisibility(newVisibility);
      setIsVisibilityFabOpen(false);
    } catch (error) {
      console.error('Error updating post visibility:', error);
      setError('Failed to update post visibility. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Check if user has accepted the protocol add disclaimer
  // Returns true if user has accepted (don't show modal), false if should show modal
  // Checks the most recent record since we always insert new rows
  const checkDisclaimerAcceptance = async (userId) => {
    if (!userId) {
      return false;
    }
    try {
      const { data, error } = await supabase
        .from('disclaimers_log')
        .select('id, accepted_at, do_not_show_again')
        .eq('user_id', userId)
        .eq('disclaimer_type', 'protocol_add')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        return false; // Show modal on error
      }

      // Show modal if no record exists (user hasn't accepted yet)
      if (!data) {
        return false; // Should show modal
      }
      
      // Don't show modal only if do_not_show_again is true
      // Even if user accepted before, if they didn't check "do not show again", show modal again
      if (data.do_not_show_again === true) {
        return true; // Don't show modal
      }
      
      // Show modal if do_not_show_again is false or null
      return false; // Should show modal
    } catch (error) {
      return false; // Show modal on error
    }
  };

  // Fetch disclaimer text from disclaimers table
  const fetchDisclaimerText = async () => {
    try {
      const { data, error } = await supabase
        .from('disclaimers')
        .select('text, version')
        .eq('type', 'protocol_add')
        .eq('latest', true)
        .maybeSingle();
      
      if (error) {
        return null;
      }

      if (!data) {
        return null;
      }

      return data;
    } catch (error) {
      return null;
    }
  };

  // Store disclaimer acceptance - always inserts new row for audit trail
  const storeDisclaimerAcceptance = async (userId, version, doNotShow) => {
    if (!userId) return false;
    try {
      // Always insert a new record (creates audit trail)
      const { error } = await supabase
        .from('disclaimers_log')
        .insert({
          user_id: userId,
          disclaimer_type: 'protocol_add',
          disclaimer_version: version,
          accepted_at: new Date().toISOString(),
          do_not_show_again: doNotShow
        });

      if (error) {
        console.error('Error storing disclaimer acceptance:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error storing disclaimer acceptance:', error);
      return false;
    }
  };

  const { hasProtocol, refresh: refreshProtocolStatuses } = useUserProtocolStatus();

  // Internal function to perform adding protocol to staged (after disclaimer check)
  const addProtocolToStagedInternal = useCallback(async (protocol) => {
    if (!userId) {
      console.log('Please sign in to add protocols');
      return false;
    }

    if (!protocol || !protocol.id) {
      console.error('Invalid protocol');
      return false;
    }

    try {
      if (hasProtocol(protocol.id)) {
        return true;
      }

      const protocolHacks = protocol.biohacks || protocol.hacks || protocol.protocol_hacks || [];
      if (protocolHacks.length === 0) {
        console.log('Cannot add a protocol with no biohacks');
        return false;
      }

      const { error: statusError } = await supabase
        .from('protocols_status')
        .insert({
          user_id: userId,
          protocol_id: protocol.id,
          is_active: false,
          added_at: new Date().toISOString()
        });

      if (statusError) {
        if (statusError.code === '23505') {
          await refreshProtocolStatuses();
          return true;
        }
        console.error('Error adding protocol to staged:', statusError);
        return false;
      }

      await refreshProtocolStatuses();
      console.log('Protocol added to your staged protocols!');
      return true;
    } catch (error) {
      console.error('Error adding protocol to staged:', error);
      return false;
    }
  }, [userId, hasProtocol, refreshProtocolStatuses]);

  // Handle adding protocol to staged protocols
  const handleAddProtocolToStaged = useCallback(async (protocol) => {
    if (!userId) {
      // Could show a toast/notification here
      console.log('Please sign in to add protocols');
      return false;
    }

    if (!protocol || !protocol.id) {
      console.error('Invalid protocol');
      return false;
    }

    // Check if user has accepted the disclaimer
    const hasAcceptedDisclaimer = await checkDisclaimerAcceptance(userId);
    
    if (!hasAcceptedDisclaimer) {
      // Fetch disclaimer text and show modal
      const disclaimer = await fetchDisclaimerText();
      if (disclaimer && disclaimer.text) {
        setDisclaimerText(disclaimer.text);
        setDisclaimerVersion(disclaimer.version || 'v1.0');
        setPendingProtocol(protocol);
        setShowDisclaimerModal(true);
        setAcceptResponsibility(false);
        setDoNotShowAgain(false);
        return false; // Don't proceed yet
      }
    }

    // User has accepted disclaimer, proceed with adding to staged
    return await addProtocolToStagedInternal(protocol);
  }, [userId, addProtocolToStagedInternal]);

  // Handle disclaimer acceptance
  const handleDisclaimerAccept = async () => {
    if (!acceptResponsibility) {
      // Required checkbox not checked
      return;
    }

    try {
      if (!userId || !pendingProtocol) {
        setShowDisclaimerModal(false);
        setPendingProtocol(null);
        return;
      }

      // Store disclaimer acceptance in database
      const acceptanceStored = await storeDisclaimerAcceptance(userId, disclaimerVersion, doNotShowAgain);
      
      if (acceptanceStored) {
        setShowDisclaimerModal(false);
        setAcceptResponsibility(false);
        setDoNotShowAgain(false);
        
        // User has accepted disclaimer, proceed with adding to staged
        const result = await addProtocolToStagedInternal(pendingProtocol);
        
        // Update UI if needed (result will be handled by ProtocolShield component)
        setPendingProtocol(null);
      } else {
        alert('Error storing disclaimer acceptance. Please try again.');
      }
    } catch (error) {
      console.error('Error handling disclaimer acceptance:', error);
      alert('Error storing disclaimer acceptance. Please try again.');
    }
  };

  const handleDisclaimerCancel = () => {
    // Just close the modal, nothing changes
    setShowDisclaimerModal(false);
    setAcceptResponsibility(false);
    setDoNotShowAgain(false);
    setPendingProtocol(null);
  };
  
  return (
    <>
    <div className={`post-card ${isOwnPost ? 'post-card--owned' : ''}`}>
      <div className="post-card-header">
        <div className="post-card-avatar">
          {post.user_profiles?.profile_picture_url ? (
            <img 
              src={post.user_profiles.profile_picture_url} 
              alt={`${username}'s profile`}
              className="post-card-avatar-image"
            />
          ) : (
            <span className="post-card-avatar-fallback">
              {username.charAt(0).toUpperCase()}
            </span>
          )}
        </div>
        <div className="post-card-user-info">
          <div className="post-card-user-row">
            <div className="post-card-user" onClick={handleUsernameClick} title={userIdForProfile ? `View ${username}'s profile` : undefined}>
              {username}
              <div className="post-card-level-badge">
                {level}
              </div>
            </div>
            {!isOwnProfile && userId && (
              <button
                className={`post-card-follow-btn ${
                  isFollowPending ? 'post-card-follow-btn-pending' : 
                  (isFollowing ? 'post-card-follow-btn-unfollow' : '')
                }`}
                onClick={handleFollowToggle}
                disabled={followLoading}
                title={isFollowPending ? 'Pending - Click to cancel' : (isFollowing ? 'Unfollow' : 'Follow')}
              >
                {followLoading ? '...' : (isFollowPending ? 'Pending' : (isFollowing ? 'Unfollow' : 'Follow'))}
              </button>
            )}
          </div>
          <div className="post-card-timestamp">
            {timeAgoLabel}
            {isEdited && <span className="edited-indicator">edited</span>}
            {/* Visibility FAB - Show on all posts, but only interactive for own posts */}
            {userId && !post.is_deleted && !editing && (
              <span className="post-card-visibility-inline">
                {userId === post.user_id ? (
                  // Own post: Show interactive button with menu
                  <>
                    <button
                      ref={visibilityFabButtonRef}
                      className="post-card-visibility-fab-btn-inline"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsVisibilityFabOpen(prev => !prev);
                      }}
                      disabled={loading}
                      title={`Visibility: ${postVisibility === 'public' ? 'Public' : postVisibility === 'followers' ? 'Followers Only' : 'Private'}`}
                      aria-label="Change post visibility"
                      aria-expanded={isVisibilityFabOpen}
                    >
                      <Icon 
                        name={postVisibility === 'public' ? 'Globe' : postVisibility === 'followers' ? 'Users' : 'Lock'} 
                        className="post-card-visibility-fab-icon-inline" 
                      />
                    </button>
                    
                    {/* Visibility FAB Menu - Only for own posts */}
                    {isVisibilityFabOpen && (
                      <div
                        ref={visibilityFabMenuRef}
                        className="post-card-visibility-fab-menu"
                        role="menu"
                        aria-orientation="vertical"
                      >
                        <button
                          type="button"
                          className={`post-card-visibility-fab-menu-item ${postVisibility === 'public' ? 'active' : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleVisibilityChange('public');
                          }}
                          disabled={loading}
                          role="menuitem"
                        >
                          <Icon name="Globe" className="post-card-visibility-fab-menu-icon" />
                          <span>Public</span>
                        </button>
                        <button
                          type="button"
                          className={`post-card-visibility-fab-menu-item ${postVisibility === 'followers' ? 'active' : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleVisibilityChange('followers');
                          }}
                          disabled={loading}
                          role="menuitem"
                        >
                          <Icon name="Users" className="post-card-visibility-fab-menu-icon" />
                          <span>Followers Only</span>
                        </button>
                        <button
                          type="button"
                          className={`post-card-visibility-fab-menu-item ${postVisibility === 'private' ? 'active' : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleVisibilityChange('private');
                          }}
                          disabled={loading}
                          role="menuitem"
                        >
                          <Icon name="Lock" className="post-card-visibility-fab-menu-icon" />
                          <span>Private</span>
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  // Other user's post: Show icon only (no interaction)
                  <span
                    className="post-card-visibility-fab-icon-only-inline"
                    title={`Visibility: ${post.visibility === 'public' ? 'Public' : post.visibility === 'followers' ? 'Followers Only' : 'Private'}`}
                  >
                    <Icon 
                      name={post.visibility === 'public' ? 'Globe' : post.visibility === 'followers' ? 'Users' : 'Lock'} 
                      className="post-card-visibility-fab-icon-inline" 
                    />
                  </span>
                )}
              </span>
            )}
          </div>
        </div>
        {post.shared_from && (
          <div className="repost-pill" title="This is a repost">
            <Icon name="Repeat" className="icon--xs icon--primary" />
          </div>
        )}
        
        {/* FAB Menu Button */}
        {userId && !post.is_deleted && !editing && (
          <button
            ref={fabButtonRef}
            className="post-card-fab-btn"
            onClick={handleFabButtonClick}
            title="More options"
            aria-label="Open post menu"
            aria-expanded={isFabMenuOpen}
          >
            <Icon name="MoreHorizontal" className="post-card-fab-icon" />
          </button>
        )}

        {/* FAB Menu */}
        {isFabMenuOpen && userId && !post.is_deleted && !editing && (
          <div 
            ref={fabMenuRef}
            className="post-card-fab-menu"
          >
            {/* Report Post - visible if not own post */}
            {userId !== post.user_id && (
              <button
                className="post-card-fab-menu-item"
                onClick={handleReportClick}
                title="Report Post"
              >
                <Icon name="AlertTriangle" className="post-card-fab-menu-icon" />
                <span>Report Post</span>
              </button>
            )}
            
            {/* Edit Post - visible if own post */}
            {userId === post.user_id && (
              <button
                className="post-card-fab-menu-item"
                onClick={handleEditClick}
                title="Edit Post"
              >
                <Icon name="Edit" className="post-card-fab-menu-icon" />
                <span>Edit Post</span>
              </button>
            )}
            
            {/* Remove Post - visible if own post */}
            {userId === post.user_id && (
              <button
                className="post-card-fab-menu-item"
                onClick={handleDeleteClick}
                title="Remove Post"
              >
                <Icon name="Trash2" className="post-card-fab-menu-icon" />
                <span>Remove Post</span>
              </button>
            )}
          </div>
        )}
      </div>
      
      {editing ? (
        <form onSubmit={e => { e.preventDefault(); handleSaveEdit(); }} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <input
            type="text"
            value={editTitle}
            onChange={e => setEditTitle(e.target.value)}
            style={{ 
              borderRadius: 12, 
              border: '1px solid var(--color-border-primary)', 
              padding: 16, 
              background: 'var(--color-surface-primary)', 
              color: 'var(--color-text-primary)', 
              fontWeight: 600, 
              fontSize: '1.1rem',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
              transition: 'none'
            }}
            placeholder="Title"
          />
          <textarea
            value={editContent}
            onChange={e => setEditContent(e.target.value)}
            style={{ 
              borderRadius: 12, 
              border: '1px solid var(--color-border-primary)', 
              padding: 16, 
              background: 'var(--color-surface-primary)', 
              color: 'var(--color-text-primary)', 
              fontSize: '1rem', 
              minHeight: 120, 
              lineHeight: '1.6',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
              transition: 'none',
              resize: 'vertical'
            }}
            placeholder="Content"
          />
          <div style={{ display: 'flex', gap: 14 }}>
            <button type="submit" className="btn btn--primary btn--sm">Save Changes</button>
            <button type="button" className="btn btn--tertiary btn--sm" onClick={handleCancelEdit}>Cancel</button>
          </div>
          {error && <div style={{ color: 'var(--color-danger)', marginTop: 10, fontSize: '0.9rem' }}>{error}</div>}
        </form>
      ) : post.is_deleted ? (
        <div className="post-card-content" style={{ color: '#b8c1ec', fontStyle: 'italic', textAlign: 'center', padding: '24px 0' }}>[deleted]</div>
      ) : (
        <>
          {/* Post Content - Show user's share text if this is a shared post */}
          {post.shared_from && (
            <>
              {post.title && (
                <div className="post-card-title">{post.title}</div>
              )}
              {post.content && (
                <div 
                  className="post-card-content"
                  dangerouslySetInnerHTML={{ __html: formatPostContentWithBiohacks(post.content, post.post_biohack_tags || []) }}
                />
              )}
              
              {/* Original Post Display – show "not visible" only when we have no original data */}
              {originalPostNotVisible && !originalPost && (
                <div className="shared-original-post shared-original-post--not-visible">
                  <div className="shared-original-post-not-visible">Original post not visible due to privacy settings</div>
                </div>
              )}
              {originalPost && (
                <div className="shared-original-post">
                  <div className="shared-original-post-header">
                    <div className="shared-original-post-avatar">
                      {originalPost.user_profiles?.profile_picture_url ? (
                        <img 
                          src={originalPost.user_profiles.profile_picture_url} 
                          alt={`${originalPost.user_profiles?.username || 'User'}'s profile`}
                          className="shared-original-post-avatar-image"
                        />
                      ) : (
                        <span className="shared-original-post-avatar-fallback">
                          {(originalPost.user_profiles?.username || 'U').charAt(0).toUpperCase()}
                        </span>
                      )}
                    </div>
                    <div className="shared-original-post-user-info">
                      <span 
                        className="shared-original-post-username"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (originalPost.user_id) {
                            navigate(`/user/${originalPost.user_id}`);
                          }
                        }}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            if (originalPost.user_id) {
                              navigate(`/user/${originalPost.user_id}`);
                            }
                          }
                        }}
                      >
                        {originalPost.user_profiles?.username || 'User'}
                      </span>
                      <span className="shared-original-post-time">
                        {timeAgo(originalPost.created_at)}
                      </span>
                    </div>
                  </div>
                  {originalPost.title && (
                    <div className="shared-original-post-title">{originalPost.title}</div>
                  )}
                  <div 
                    className="shared-original-post-content"
                    dangerouslySetInnerHTML={{ __html: formatPostContentWithBiohacks(originalPost.content, originalPost.post_biohack_tags || []) }}
                  />
                  
                  {/* Show attachments from original post */}
                  {(() => {
                    const originalModules = [];
                    
                    // Add protocols
                    if (originalPost.post_attachments) {
                      const protocolAttachments = originalPost.post_attachments
                        .filter(att => att.type === 'protocols')
                        .flatMap(att => (att.post_attachment_protocols || [])
                          .map(pap => pap.protocols)
                          .filter(p => p && p.id)
                          .map(protocol => ({
                            type: 'protocol',
                            data: protocol,
                            id: protocol.id,
                            sort_order: att.sort_order || 0
                          }))
                        );
                      originalModules.push(...protocolAttachments);
                    }
                    
                    // Add images
                    if (originalPost.post_attachments) {
                      const imageAttachments = originalPost.post_attachments
                        .filter(att => att.type === 'image')
                        .flatMap(att => (att.post_attachment_images || []).map(img => ({
                          type: 'image',
                          data: img,
                          id: img.id,
                          sort_order: att.sort_order || 0
                        })));
                      originalModules.push(...imageAttachments);
                    }
                    
                    // Add protocol reports
                    if (originalPost.post_attachments) {
                      const protocolReportAttachments = originalPost.post_attachments
                        .filter(att => att.type === 'report')
                        .flatMap(att => (att.post_attachment_reports || [])
                          .map(prAtt => prAtt.protocol_report_snapshots)
                          .filter(Boolean)
                          .map(pr => ({
                            type: 'protocolReport',
                            data: pr,
                            id: 'protocol-report-' + pr.id,
                            sort_order: att.sort_order || 0
                          }))
                        );
                      originalModules.push(...protocolReportAttachments);
                    }
                    
                    // Add biomarkers (original post author's gender for ranges)
                    const originalAuthorGender = originalPost.user_profiles?.gender;
                    if (originalPost.post_attachments && originalPost.post_attachments.some(att => att.type === 'biomarker')) {
                      const biomarkerAttachments = originalPost.post_attachments.filter(att => att.type === 'biomarker');
                      biomarkerAttachments.forEach(attachment => {
                        const biomarkerData = (attachment.post_attachment_biomarkers || [])
                          .map(biomarkerAtt => biomarkerAtt.biomarkers)
                          .filter(Boolean);
                        if (biomarkerData.length > 0) {
                          biomarkerData.forEach(biomarker => {
                            const enrichedBiomarker = enrichBiomarkerWithRanges(biomarker, originalAuthorGender);
                            const labResults = enrichedBiomarker.lab_results || [];
                            const mostRecentResult = labResults.length > 0 ? labResults[0] : null;
                            originalModules.push({
                              type: 'biomarker',
                              data: {
                                ...enrichedBiomarker,
                                result: mostRecentResult ? {
                                  ...mostRecentResult,
                                  panel: mostRecentResult.lab_panels
                                } : null,
                                resultDeleted: enrichedBiomarker.resultDeleted || false
                              },
                              id: 'biomarker-' + enrichedBiomarker.id,
                              sort_order: attachment.sort_order || 0
                            });
                          });
                        }
                      });
                    }
                    
                    if (originalModules.length === 0) return null;
                    
                    const sortedModules = originalModules.sort((a, b) => a.sort_order - b.sort_order);
                    
                    return (
                      <div className="shared-original-post-attachments">
                        <AttachmentDisplay
                          modules={sortedModules}
                          mode="display"
                          onBiohackClick={(biohack) => {
                            setSelectedBiohack(biohack);
                            setShowBiohackInfo(true);
                          }}
                          userVotes={userVotes}
                          handleVote={handleVote}
                          onAddProtocolToStaged={handleAddProtocolToStaged}
                        />
                      </div>
                    );
                  })()}
                </div>
              )}
            </>
          )}
          
          {/* Regular post content (not a shared post) */}
          {!post.shared_from && (
            <>
              {post.title && (
                <div className="post-card-title">{post.title}</div>
              )}
              <div 
                className="post-card-content"
                dangerouslySetInnerHTML={{ __html: formattedContent }}
              />
            </>
          )}
          
          {/* Show all modules (protocols, biohacks, and images) in a single row with horizontal scrolling */}
          {(() => {
            // Collect all modules with their sort order from the database
            const allModules = [];
            
            // Add protocols with their sort order
            if (post.attachedProtocols && post.attachedProtocols.length > 0) {
              post.attachedProtocols.forEach(protocol => {
                if (protocol && protocol.id) {
                  // Find the corresponding post_attachment to get the sort_order
                  const attachment = post.post_attachments?.find(att => 
                    att.type === 'protocols' && 
                    att.post_attachment_protocols?.some(pap => pap.protocols?.id === protocol.id)
                  );
                  
                  allModules.push({
                    type: 'protocol',
                    data: protocol,
                    id: protocol.id,
                    sort_order: attachment?.sort_order || 0
                  });
                }
              });
            }
            
            // Biohack attachment cards removed per updated requirements
            
            // Add images with their sort order from the database
            if (post.post_attachments && post.post_attachments.some(att => att.type === 'image')) {
              const imageAttachments = post.post_attachments
                .filter(att => att.type === 'image')
                .flatMap(att => (att.post_attachment_images || []).map(img => ({
                  ...img,
                  sort_order: att.sort_order || 0
                })));
              
              // Each image gets its own individual module
              imageAttachments.forEach(image => {
                allModules.push({
                  type: 'image',
                  data: [image], // Wrap single image in array for consistency
                  id: 'image-' + image.id,
                  sort_order: image.sort_order
                });
              });
            }
            
            // Add biomarkers with their sort order
            if (post.post_attachments && post.post_attachments.some(att => att.type === 'biomarker')) {
              const biomarkerAttachments = post.post_attachments.filter(att => att.type === 'biomarker');
              
              biomarkerAttachments.forEach(attachment => {
                const biomarkerData = (attachment.post_attachment_biomarkers || [])
                  .map(biomarkerAtt => biomarkerAtt.biomarkers)
                  .filter(Boolean);
                
                if (biomarkerData.length > 0) {
                  biomarkerData.forEach(biomarker => {
                    // Enrich biomarker with gender-specific ranges
                    const enrichedBiomarker = enrichBiomarkerWithRanges(biomarker, postAuthorGender);
                    
                    // Process the biomarker data to match what the preview expects
                    const labResults = enrichedBiomarker.lab_results || [];
                    const mostRecentResult = labResults.length > 0 ? labResults[0] : null;
                    
                    allModules.push({
                      type: 'biomarker',
                      data: {
                        ...enrichedBiomarker,
                        result: mostRecentResult ? {
                          ...mostRecentResult,
                          panel: mostRecentResult.lab_panels
                        } : null,
                        resultDeleted: enrichedBiomarker.resultDeleted || false
                      },
                      id: 'biomarker-' + enrichedBiomarker.id,
                      sort_order: attachment.sort_order || 0
                    });
                  });
                }
              });
            }
            
            // Add protocol reports with their sort order
            if (post.post_attachments && post.post_attachments.some(att => att.type === 'report')) {
              const protocolReportAttachments = post.post_attachments.filter(att => att.type === 'report');
              
              protocolReportAttachments.forEach(attachment => {
                const protocolReportData = (attachment.post_attachment_reports || [])
                  .map(prAtt => prAtt.protocol_report_snapshots)
                  .filter(Boolean);
                
                if (protocolReportData.length > 0) {
                  protocolReportData.forEach(protocolReport => {
                    allModules.push({
                      type: 'protocolReport',
                      data: protocolReport,
                      id: 'protocol-report-' + protocolReport.id,
                      sort_order: attachment.sort_order || 0
                    });
                  });
                }
              });
            }
            
            // Sort all modules by their database sort_order
            allModules.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
            
            // All modules are now in a single row with horizontal scrolling
            // No special layout rules needed - just use the sorted modules
            const finalModules = allModules;
            
            if (finalModules.length === 0) return null;
            
            // Show all modules in a single row with horizontal scrolling
            const visibleModules = finalModules;
            
            return (
              <div className="post-card-attachments">
                <AttachmentDisplay
                  modules={visibleModules}
                  mode="display"
                  onBiohackClick={(biohack) => {
                    setSelectedBiohack(biohack);
                    setShowBiohackInfo(true);
                  }}
                  userVotes={userVotes}
                  handleVote={handleVote}
                  onAddProtocolToStaged={handleAddProtocolToStaged}
                />
              </div>
            );
          })()}
        </>
      )}
      
      <div className="post-card-footer">
        <div className="post-card-footer-left">
          <button 
            className={`post-card-stat ${liked ? 'post-card-stat--liked' : ''}`}
            onClick={handleLikeToggle}
            disabled={!userId}
            title={userId ? (liked ? 'Unlike' : 'Like') : 'Sign in to like posts'}
          >
            <Icon 
              name="ThumbsUp" 
              className={`icon--sm ${liked ? 'icon--primary' : 'icon--muted'}`}
            />
            <span style={{ fontWeight: 700 }}>{localLikesCount}</span>
          </button>
          
          <button 
            className={`post-card-stat ${showComments ? 'post-card-stat--comments-active' : ''}`}
            onClick={() => setShowComments((v) => !v)}
            title={showComments ? 'Hide comments' : 'Show comments'}
          >
            <Icon name="MessageCircle" className={`icon--sm ${showComments ? 'icon--primary' : 'icon--muted'}`} />
            <span style={{ fontWeight: 700 }}>{localCommentsCount}</span>
          </button>
          
          <button 
            className={`post-card-stat ${reposted ? 'post-card-stat--reposted' : ''}`}
            onClick={handleRepost}
            disabled={!userId || repostLoading}
            title={userId ? (reposted ? 'Remove repost' : 'Repost this post') : 'Sign in to repost'}
          >
            <Icon 
              name="Repeat"
              className={`icon--sm ${reposted ? 'icon--primary' : 'icon--muted'}`}
            />
            <span style={{ fontWeight: 700 }}>{localRepostCount}</span>
          </button>
          
          {/* Biotags button - only show if there are biotags */}
          {(() => {
            const biohackCount = post.post_biohack_tags?.length || 0;
            const biomarkerCount = post.post_biomarker_tags?.length || 0;
            const totalBiotagsCount = biohackCount + biomarkerCount;
            
            if (totalBiotagsCount === 0) return null;
            
            return (
              <>
                <button 
                  ref={biotagsButtonRef}
                  className={`post-card-stat post-card-stat--tags ${showBiotagsDropdown ? 'post-card-stat--tags-active' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowBiotagsDropdown(prev => !prev);
                  }}
                  title="View biotags"
                >
                  <Icon 
                    name="Tag"
                    className={`icon--sm ${showBiotagsDropdown ? 'icon--primary' : 'icon--muted'}`}
                  />
                  <span style={{ fontWeight: 700 }}>{totalBiotagsCount}</span>
                </button>
              
              {/* Biotags Dropdown */}
              {showBiotagsDropdown && (
                <div 
                  ref={biotagsDropdownRef}
                  className="post-card-biotags-dropdown"
                  role="menu"
                  aria-orientation="vertical"
                >
                  <div className="post-card-biotags-dropdown-content">
                    {/* Biohack tags */}
                    {post.post_biohack_tags && post.post_biohack_tags.map((tag) => {
                      const biohack = tag.biohacks;
                      if (!biohack || !biohack.name) return null;
                      return (
                        <span 
                          key={biohack.id} 
                          className="biohack-tag biohack-tag--clickable"
                          onClick={() => {
                            setSelectedBiohack(biohack);
                            setShowBiohackInfo(true);
                            setShowBiotagsDropdown(false);
                          }}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setSelectedBiohack(biohack);
                              setShowBiohackInfo(true);
                              setShowBiotagsDropdown(false);
                            }
                          }}
                        >
                          {biohack.name}
                        </span>
                      );
                    })}
                    {/* Biomarker tags */}
                    {post.post_biomarker_tags && post.post_biomarker_tags.map((tag) => {
                      const biomarker = tag.biomarkers;
                      if (!biomarker || !biomarker.name) return null;
                      return (
                        <span 
                          key={biomarker.id} 
                          className="biohack-tag biohack-tag--biomarker biohack-tag--clickable"
                          role="button"
                          tabIndex={0}
                        >
                          {biomarker.name}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
              </>
            );
          })()}
          
          <button 
            className="post-card-stat post-card-stat--share"
            onClick={() => setShowShareModal(true)}
            title="Share post"
          >
            <Icon 
              name="Send"
              className="icon--sm icon--muted"
            />
            <span style={{ fontWeight: 700 }}>Share</span>
          </button>
        </div>
        
        <div className="post-card-footer-right">
        </div>
      </div>
      
      {showComments && (
        <CommentSection 
          postId={post.id} 
          postAuthorId={post.user_id} 
          onCommentAdded={onCountsChanged}
          onCommentSubmit={(fn) => { commentRefreshFnRef.current = fn; }}
          onCommentCountChange={handleCommentCountChange}
        />
      )}
      {showBiohackInfo && selectedBiohack && (
        <BiohackInfoCard
          biohack={selectedBiohack}
          onClose={() => setShowBiohackInfo(false)}
          isFavourited={favourites.some(fav => fav.hack_id === selectedBiohack.id)}
          onToggleFavourite={() => handleToggleFavourite(selectedBiohack.id)}
          hasRated={ratedBiohackIds.has(selectedBiohack.id)}
          onRatingSubmitted={() => {
            setRatedBiohackIds(prev => new Set([...prev, selectedBiohack.id]));
          }}
        />
      )}
      {showReportModal && (
        <ReportModal
          isOpen={showReportModal}
          onClose={() => setShowReportModal(false)}
          post={post}
          comment={null}
          message={null}
          reportedUserId={post.user_id}
          reportedUsername={username}
        />
      )}
      {showShareModal && (
        <SharePostModal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          post={post}
          onShare={handleSharePost}
        />
      )}
    </div>
    {showComments && (
      <div className="comment-input-wrapper">
        <div className="comment-input-connector"></div>
        <form onSubmit={handleAddComment} className="comment-input-external">
          <input
            type="text"
            value={newComment}
            onChange={e => setNewComment(e.target.value)}
            placeholder={userId ? 'Add a comment...' : 'Sign in to comment'}
            disabled={!userId || submittingComment}
          />
          {newComment.trim() && (
            <button 
              type="submit" 
              disabled={!userId || !newComment.trim() || submittingComment} 
              className="btn btn--primary btn--sm"
            >
              {submittingComment ? '...' : 'Post'}
            </button>
          )}
        </form>
      </div>
    )}

      {/* Protocol Add Disclaimer Modal */}
      {showDisclaimerModal && (
        <div className="modal-overlay" onClick={(e) => {
          // Don't close on overlay click - user must explicitly choose
          e.stopPropagation();
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h3>Protocol Add Disclaimer</h3>
            </div>
            <div className="modal-body" style={{ padding: '24px' }}>
              <div style={{ 
                marginBottom: '24px', 
                fontSize: '14px', 
                lineHeight: '1.6', 
                color: 'var(--color-text-primary)',
                whiteSpace: 'pre-line'
              }}>
                {disclaimerText}
              </div>
              
              <div style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'flex-start', 
                  gap: '12px', 
                  cursor: 'pointer',
                  fontSize: '14px',
                  lineHeight: '1.5',
                  color: 'var(--color-text-primary)'
                }}>
                  <input
                    type="checkbox"
                    checked={acceptResponsibility}
                    onChange={(e) => setAcceptResponsibility(e.target.checked)}
                    style={{ 
                      marginTop: '2px',
                      width: '18px',
                      height: '18px',
                      cursor: 'pointer',
                      flexShrink: 0
                    }}
                  />
                  <span>I understand and accept responsibility for adding this protocol to staged</span>
                </label>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'flex-start', 
                  gap: '12px', 
                  cursor: 'pointer',
                  fontSize: '14px',
                  lineHeight: '1.5',
                  color: 'var(--color-text-primary)'
                }}>
                  <input
                    type="checkbox"
                    checked={doNotShowAgain}
                    onChange={(e) => setDoNotShowAgain(e.target.checked)}
                    style={{ 
                      marginTop: '2px',
                      width: '18px',
                      height: '18px',
                      cursor: 'pointer',
                      flexShrink: 0
                    }}
                  />
                  <span>Do not show again</span>
                </label>
              </div>

              <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                <button
                  type="button"
                  className="btn btn--secondary btn--md btn--rounded"
                  onClick={handleDisclaimerCancel}
                  style={{ flex: 1 }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn--primary btn--md btn--rounded"
                  onClick={handleDisclaimerAccept}
                  disabled={!acceptResponsibility}
                  style={{ flex: 1 }}
                >
                  Accept
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PostCard; 